import axios from 'axios';

import { assertClientSide, webpack } from '@dr.pogodin/react-utils';

import type * as TusClientServiceM from 'client/services/tus';

import type {
  AllowedUpdateT,
  GetVideoResT,
  ListVideosQueryT,
  ListVideosResT,
  UpdateVideoBodyT,
  UpdateVideoResT,
} from 'server/controllers/video';

import type { VideoUploadBodyT, VideoUploadResT } from 'server/controllers/video/upload';

import type * as VideoM from 'server/services/video';
import { P_API_VIDEO, getConfig } from 'utils';
import type { VideoT } from 'utils/video';

const ss = webpack.requireWeak<typeof VideoM>('server/services/video', import.meta.dirname);

/**
 * Gets a single video by its MongoDB ID in our database.
 */
export async function getVideo(id: string): Promise<VideoT> {
  if (ss) return ss.getVideo(id);

  const res = await axios.get<GetVideoResT>(`${P_API_VIDEO}/${id}`);

  return res.data;
}

export type GetVideosOptionsT = ListVideosQueryT;

export async function listVideos(
  options: GetVideosOptionsT = {},
): Promise<ListVideosResT> {
  if (ss) {
    // MIND: ss.getVideos() below fails on purpose if its argument contains
    // unexpected keys.
    const { getTotalCount, ...rest } = options;

    return {
      totalCount: getTotalCount ? await ss.countVideos(rest) : undefined,
      videos: await ss.listVideos(rest),
    };
  }

  const res = await axios.get<ListVideosResT>(P_API_VIDEO, { params: options });

  return res.data;
}

export async function updateVideoData(
  id: string,
  update: Partial<Pick<VideoT, AllowedUpdateT>> & {
    artistId?: string;
  },
  runAiAnalysis?: boolean,
): Promise<VideoT> {
  assertClientSide();

  const config = await getConfig();

  const res = await axios.patch<UpdateVideoResT>(`${P_API_VIDEO}/${id}`, {
    _csrf: config.CSRF,
    ...update,
    runAiAnalysis,
  } satisfies UpdateVideoBodyT);

  return res.data;
}

export type UploadVideoOptionsT = {
  artistId: string;
  file: File;

  /** Optional handler of progress signals.
   *  @param progress Upload progress, as a number within [0,1] range. */
  onProgress?: (progress: number) => void;

  videoId?: string;
};

/**
 * (Re-)uploads video to Vimeo.
 */
export async function uploadVideo({
  artistId,
  file,
  onProgress,
  videoId,
}: UploadVideoOptionsT): Promise<VideoT> {
  assertClientSide();

  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { tusUpload } = require('client/services/tus') as typeof TusClientServiceM;

  let url = P_API_VIDEO;
  if (videoId) url += `/${videoId}`;

  const config = await getConfig();

  const res = await axios.post<VideoUploadResT>(url, {
    _csrf: config.CSRF,
    artistId,
    size: file.size,
  } satisfies VideoUploadBodyT);

  if (!res.data.uploadUrl) throw Error('Internal error');

  await tusUpload({ data: file, onProgress, url: res.data.uploadUrl });

  return res.data.video;
}
